<div class="roles-management-page">
  <!-- Header -->
  <div class="page-header">
    <div class="header-content">
      <button mat-icon-button (click)="onCancel()" class="back-btn">
        <mat-icon>arrow_back</mat-icon>
      </button>

      <div class="user-info">
        <div class="user-avatar">
          <div class="avatar-circle" [style.background-color]="getAvatarColor($safeNavigationMigration(user?.username))">
            {{ getUserInitials($safeNavigationMigration(user?.firstName), $safeNavigationMigration(user?.lastName)) }}
          </div>
        </div>
        <div class="user-details">
          <h1>Gestion des Rôles et Habilitations</h1>
          <div class="user-info-details">
            <span class="user-name">{{ user?.firstName }} {{ user?.lastName }}</span>
            <span class="user-separator">•</span>
            <span class="user-email">{{ user?.email }}</span>
            <span class="user-separator">•</span>
            <span class="user-username">&#64;{{ user?.username }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Indicateur de modifications -->
    @if (hasChanges) {
      <div class="changes-indicator">
        <div class="changes-content">
          <mat-icon>edit_note</mat-icon>
          <span class="changes-text">{{ pendingChanges.length }} modification(s) en attente</span>
          <button mat-stroked-button (click)="resetAll()" class="reset-btn">
            <mat-icon>refresh</mat-icon>
            Tout réinitialiser
          </button>
        </div>
      </div>
    }
  </div>

  <!-- Contenu principal avec deux colonnes -->
  <div class="page-content">
    <div class="content-grid">
      <!-- Colonne de gauche : Recherche et applications -->
      <div class="left-column">
        <mat-card class="search-card">
          <div class="search-section">
            <mat-form-field appearance="outline" class="search-apps-field">
              <mat-label>Rechercher une application</mat-label>
              <input matInput [(ngModel)]="appSearchTerm" (ngModelChange)="onSearchChange($event)"
                placeholder="Nom de l'application...">
                <mat-icon matPrefix>search</mat-icon>
                @if (appSearchTerm) {
                  <button matSuffix mat-icon-button (click)="appSearchTerm = ''; onSearchChange('')">
                    <mat-icon>clear</mat-icon>
                  </button>
                }
              </mat-form-field>
            </div>

            <!-- Statistiques -->
            <div class="stats-container">
              <div class="stat-item">
                <mat-icon>apps</mat-icon>
                <div class="stat-info">
                  <span class="stat-value">{{ applications.length }}</span>
                  <span class="stat-label">Applications</span>
                </div>
              </div>
              <div class="stat-item">
                <mat-icon>badge</mat-icon>
                <div class="stat-info">
                  <span class="stat-value">{{ getTotalRoles() }}</span>
                  <span class="stat-label">Rôles totaux</span>
                </div>
              </div>
              <div class="stat-item">
                <mat-icon>check_circle</mat-icon>
                <div class="stat-info">
                  <span class="stat-value">{{ getTotalSelectedRoles() }}</span>
                  <span class="stat-label">Rôles sélectionnés</span>
                </div>
              </div>
            </div>
          </mat-card>

          <!-- Liste des applications -->
          <mat-card class="applications-card">
            <div class="card-header">
              <h2>Applications</h2>
              <span class="apps-count">{{ filteredApplications.length }} application(s)</span>
            </div>

            <div class="applications-container">
              <!-- Loader pendant le chargement -->
              @if (isLoading) {
                <div class="loading-applications">
                  <mat-icon class="loading-spinner">hourglass_empty</mat-icon>
                  <div class="loading-text">
                    <p>Chargement des applications et rôles...</p>
                    <span class="loading-subtitle">Veuillez patienter</span>
                  </div>
                </div>
              }

              <!-- Aucune application trouvée -->
              @if (!isLoading && filteredApplications.length === 0) {
                <div class="no-apps-found">
                  <mat-icon>search_off</mat-icon>
                  <h3>Aucune application trouvée</h3>
                  <p>Essayez de modifier vos termes de recherche</p>
                </div>
              }

              <!-- Liste des applications -->
              @if (!isLoading && filteredApplications.length > 0) {
                <mat-accordion multi class="apps-accordion">
                  @for (app of filteredApplications; track app.id) {
                    <mat-expansion-panel [expanded]="isAppExpanded(app)" class="app-panel"
                      [class.has-selected-roles]="getSelectedRolesCount(app) > 0">

                      <mat-expansion-panel-header class="app-header">
                        <mat-panel-title class="app-title-container">
                          <mat-checkbox [checked]="isAppFullySelected(app)" [indeterminate]="isAppPartiallySelected(app)"
                            (change)="toggleAppSelection(app, $event)" (click)="$event.stopPropagation()" color="#45B56F"
                            class="app-checkbox">
                          </mat-checkbox>

                          <div class="app-info">
                            <mat-icon class="app-icon">{{ app.icon || 'apps' }}</mat-icon>
                            <div class="app-text">
                              <span class="app-name">{{ app.name }}</span>
                              @if (app.description) {
                                <span class="app-description">{{ app.description }}</span>
                              }
                            </div>
                          </div>

                          <span class="roles-badge">
                            {{ getSelectedRolesCount(app) }}/{{ app.roles?.length || 0 }}
                          </span>
                        </mat-panel-title>
                      </mat-expansion-panel-header>

                      <div class="roles-list-container">
                        <div class="roles-header">
                          <h4>Rôles disponibles</h4>
                          <button mat-button (click)="selectAllAppRoles(app)" class="select-all-btn">
                            <mat-icon>checklist</mat-icon>
                            {{ isAppFullySelected(app) ? 'Tout désélectionner' : 'Tout sélectionner' }}
                          </button>
                        </div>

                        @if (!app.roles || app.roles.length === 0) {
                          <div class="no-roles">
                            <mat-icon>info</mat-icon>
                            <p>Aucun rôle disponible pour cette application</p>
                          </div>
                        }

                        @if (app.roles && app.roles.length > 0) {
                          <!-- Dans la partie roles-grid -->
                          <div class="roles-grid">
                            @for (role of app.roles; track role.id) {
                              <div class="role-item" [class.selected]="isRoleSelected(app, role)">
                                <mat-checkbox [checked]="isRoleSelected(app, role)"
                                  (change)="toggleRoleSelection(app, role, $event)" color="#45B56F" class="role-checkbox">
                                  <div class="role-content">
                                    <span class="role-name">{{ role.title || role.name }}</span>
                                    @if (role.description) {
                                      <span class="role-description">{{ role.description }}</span>
                                    }
                                  </div>
                                </mat-checkbox>
                              </div>
                            }
                          </div>
                        }
                      </div>
                    </mat-expansion-panel>
                  }
                </mat-accordion>
              }
            </div>
          </mat-card>
        </div>

        <!-- Colonne de droite : Résumé et actions -->
        <div class="right-column">
          <mat-card class="summary-card">
            <div class="summary-header">
              <mat-icon>summarize</mat-icon>
              <h2>Résumé</h2>
            </div>

            <div class="summary-content">
              <div class="summary-item">
                <mat-icon>person</mat-icon>
                <div class="summary-text">
                  <span class="summary-label">Utilisateur</span>
                  <span class="summary-value">{{ user?.firstName }} {{ user?.lastName }}</span>
                </div>
              </div>

              <div class="summary-item">
                <mat-icon>apps</mat-icon>
                <div class="summary-text">
                  <span class="summary-label">Applications</span>
                  <span class="summary-value">{{ applications.length }} au total</span>
                </div>
              </div>

              <div class="summary-item">
                <mat-icon>check_circle</mat-icon>
                <div class="summary-text">
                  <span class="summary-label">Rôles attribués / Rôles totaux</span>
                  <span class="summary-value">{{ getTotalSelectedRoles() }} / {{ getTotalRoles() }}</span>
                </div>
              </div>

              <div class="summary-item highlight">
                <mat-icon>edit_note</mat-icon>
                <div class="summary-text">
                  <span class="summary-label">Modifications en attente</span>
                  <span class="summary-value">{{ pendingChanges.length }}</span>
                </div>
              </div>
            </div>

            <!-- Actions en bas de la colonne de droite -->
            <div class="actions-footer">
              <button mat-stroked-button (click)="onCancel()" class="cancel-btn" [disabled]="isSaving">
                <mat-icon>close</mat-icon>
                Annuler
              </button>
              <button mat-flat-button color="#45B56F" (click)="onSave()" [disabled]="!hasChanges || isSaving"
                class="save-btn">
                @if (!isSaving) {
                  <mat-icon>save</mat-icon>
                }
                @if (isSaving) {
                  <mat-icon class="loading-icon">hourglass_empty</mat-icon>
                }
                {{ isSaving ? 'Enregistrement...' : 'Enregistrer' }}
              </button>
            </div>
          </mat-card>
        </div>
      </div>
    </div>
  </div>